import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SplashScreen from '../components/common/SplashScreen';
import { historyAPI, roundsAPI, getImageUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../utils/constants';

export default function History() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSplash, setShowSplash] = useState(false);
  const [checkingActiveRound, setCheckingActiveRound] = useState(true);

  useEffect(() => {
    checkForActiveRound();
    fetchHistoryEntries();
  }, []);

  // Restore scroll position when returning to history index
  useEffect(() => {
    if (!loading && entries.length > 0) {
      const savedScrollPosition = sessionStorage.getItem('historyScrollPosition');
      if (savedScrollPosition) {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
          sessionStorage.removeItem('historyScrollPosition');
        }, 0);
      }
    }
  }, [loading, entries]);

  const checkForActiveRound = async () => {
    try {
      // Check if splash has already been shown this session
      const splashShown = sessionStorage.getItem('splashShown');

      if (splashShown) {
        // Already shown this session, skip it
        setShowSplash(false);
        setCheckingActiveRound(false);
        return;
      }

      const activeRound = await roundsAPI.getActive();
      // If there's an active round, skip splash screen
      if (activeRound && activeRound.id) {
        setShowSplash(false);
      } else {
        // No active round - show splash screen
        setShowSplash(true);
        // Mark as shown for this session
        sessionStorage.setItem('splashShown', 'true');
      }
    } catch (err) {
      // No active round or error - show splash screen
      const splashShown = sessionStorage.getItem('splashShown');
      if (!splashShown) {
        setShowSplash(true);
        sessionStorage.setItem('splashShown', 'true');
      }
    } finally {
      setCheckingActiveRound(false);
    }
  };

  const fetchHistoryEntries = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await historyAPI.getAll();
      // Sort by year descending (most recent first)
      const sorted = Array.isArray(data)
        ? data.sort((a, b) => b.year - a.year)
        : [];
      setEntries(sorted);
    } catch (err) {
      console.error('Error loading history entries:', err);
      setError('Failed to load history entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewEntry = (year) => {
    // Save current scroll position before navigating
    sessionStorage.setItem('historyScrollPosition', window.scrollY.toString());
    navigate(`/history/${year}`);
  };

  const handleCreateNew = () => {
    navigate('/history/new');
  };

  // Show splash screen if not checking and splash should show
  if (!checkingActiveRound && showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pb-8">
      {error && (
        <div className="bg-red-600 text-white px-4 py-3 mb-4 mx-4 md:mx-8 lg:mx-12 max-w-4xl mx-auto">
          {error}
        </div>
      )}

      <div className="px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
        {/* History Entries */}
        {entries.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📖</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No History Yet</h2>
              <p className="text-lg text-gray-600">
                History entries will appear here once created.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Most Recent Champion - Side by Side Card */}
            {entries[0] && (
              <button
                onClick={() => handleViewEntry(entries[0].year)}
                className="flex flex-col md:flex-row gap-6 w-full bg-white rounded-xl border border-gray-300 p-6 hover:shadow-lg transition-all duration-300 group text-left"
              >
                {/* Text Content - Left Side */}
                <div className="flex-1 flex flex-col justify-center">
                  {/* Year */}
                  <div className="text-5xl md:text-6xl font-light text-gray-400 mb-2">
                    {entries[0].year}
                  </div>

                  {/* Course Name */}
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                    {entries[0].course_name}
                  </h2>

                  {/* Subtitle */}
                  <p className="text-xs text-gray-500 tracking-wider uppercase mb-2">
                    Champion Golfer of the Year
                  </p>

                  {/* Champion Name */}
                  <p className="text-xl md:text-2xl font-bold text-gray-900">
                    {entries[0].champion_name}
                  </p>
                </div>

                {/* Winner Photo - Right Side */}
                {entries[0].image_url && (
                  <div className="w-full md:w-[400px] flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={getImageUrl(entries[0].image_url)}
                      alt={`${entries[0].year} Champion`}
                      className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
              </button>
            )}

            {/* Previous Years - 2 Column Grid */}
            {entries.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {entries.slice(1).map((entry) => (
                  <button
                    key={entry.id}
                    onClick={() => handleViewEntry(entry.year)}
                    className="bg-white border border-gray-300 rounded-xl p-6 text-left hover:shadow-lg hover:border-primary transition-all duration-200 group"
                  >
                    {/* Year */}
                    <div className="text-3xl text-gray-400 font-light mb-2">
                      {entry.year}
                    </div>

                    {/* Course Name */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                      {entry.course_name}
                    </h3>

                    {/* Subtitle */}
                    <p className="text-xs text-gray-500 tracking-wider uppercase mb-1">
                      Champion Golfer of the Year
                    </p>

                    {/* Champion Name */}
                    <p className="text-base font-bold text-gray-900 mb-3">
                      {entry.champion_name}
                    </p>

                    {/* Read More Link */}
                    <div className="text-sm text-gray-600 group-hover:text-primary transition-colors">
                      Read more
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin Controls - Moved to Bottom */}
        {isAuthenticated && (
          <div className="mt-8">
            <Card>
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Manage History</h2>
                <Button
                  variant="primary"
                  onClick={handleCreateNew}
                  className="text-sm"
                >
                  + New Entry
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
